import React from 'react';
import { motion } from 'framer-motion';
import { highlightTerms } from '../highlightTerms';
import { ArrowRight } from 'lucide-react';

export const StatSlide = ({ slide }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const bulletVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
  };

  return (
    <div
      className="h-full w-full flex flex-col justify-center items-center px-8 md:px-12 lg:px-20 py-12"
      data-testid={`slide-${slide.id}`}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-5xl w-full text-center"
      >
        <motion.h2
          variants={itemVariants}
          className="pres-font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white mb-16"
        >
          {highlightTerms(slide.title)}
        </motion.h2>

        {/* Formulas */}
        {slide.formulas && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {slide.formulas.map((item, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="relative group"
              >
                <div className="bg-[#2A1D45]/60 backdrop-blur-md border border-[#D31113]/30 rounded-2xl p-8 group-hover:border-[#D31113] transition-colors overflow-hidden">
                  <div className="font-bold text-white mb-4" style={{ fontFamily: "'Inter', monospace", letterSpacing: '0.02em', fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>
                    {item.formula}
                  </div>
                  <p className="font-sans text-base md:text-lg text-gray-400">
                    {highlightTerms(item.label)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Bullets */}
        {slide.bullets && (
          <motion.div className="space-y-4 max-w-2xl mx-auto mb-12">
            {slide.bullets.map((bullet, idx) => (
              <motion.div
                key={idx}
                variants={bulletVariants}
                className="flex items-center gap-4 text-left"
              >
                <ArrowRight className="w-6 h-6 text-[#D31113] flex-shrink-0" />
                <p className="font-sans text-lg md:text-xl text-gray-200">
                  {highlightTerms(bullet)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Key Line */}
        {slide.keyLine && (
          <motion.div
            variants={itemVariants}
            className="mt-8 py-6 px-8 bg-[#D31113]/10 border border-[#D31113]/40 rounded-xl inline-block"
          >
            <p className="font-sans text-xl md:text-2xl text-white font-bold">
              {highlightTerms(slide.keyLine)}
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};
